import { useState } from 'react';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import type {
  MissingSerial,
  StockAuditScan,
} from '../../../types/stockAudit';
import {
  SCAN_RESULT_LABELS,
  isUnexpectedResult,
} from './scanResultLabels';

type ResultTab = 'missing' | 'unexpected' | 'found' | 'outOfScope';

interface AuditResultTabsProps {
  missing: MissingSerial[];
  scans: StockAuditScan[];
  canEdit: boolean;
  onRemoveScan: (scanId: number) => void;
}

interface ResultRow {
  key: string;
  serialNumber: string;
  secondary: string;
  scanId?: number;
}

const missingToRow = (item: MissingSerial): ResultRow => ({
  key: `missing-${item.serialNumber}`,
  serialNumber: item.serialNumber,
  secondary: item.productName ?? '',
});

const scanToRow = (scan: StockAuditScan, withReason: boolean): ResultRow => ({
  key: `scan-${scan.id}`,
  serialNumber: scan.serialNumber,
  secondary: [scan.productName, withReason ? SCAN_RESULT_LABELS[scan.result] : undefined]
    .filter(Boolean)
    .join(' · '),
  scanId: scan.id,
});

const EMPTY_MESSAGES: Record<ResultTab, string> = {
  missing: 'Всі очікувані одиниці знайдено',
  unexpected: 'Зайвих одиниць немає',
  found: 'Ще нічого не знайдено',
  outOfScope: 'Немає',
};

export default function AuditResultTabs({
  missing,
  scans,
  canEdit,
  onRemoveScan,
}: AuditResultTabsProps) {
  const [tab, setTab] = useState<ResultTab>('missing');

  const unexpected = scans.filter((scan) => isUnexpectedResult(scan.result));
  const found = scans.filter((scan) => scan.result === 'FOUND');
  const outOfScope = scans.filter((scan) => scan.result === 'OUT_OF_SCOPE');

  const rowsByTab: Record<ResultTab, ResultRow[]> = {
    missing: missing.map(missingToRow),
    unexpected: unexpected.map((scan) => scanToRow(scan, true)),
    found: found.map((scan) => scanToRow(scan, false)),
    outOfScope: outOfScope.map((scan) => scanToRow(scan, false)),
  };

  const rows = rowsByTab[tab];

  return (
    <Paper sx={{ mb: 2 }}>
      <Tabs
        value={tab}
        onChange={(_event, value: ResultTab) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab
          label={`Відсутні (${missing.length})`}
          value="missing"
        />
        <Tab
          label={`Зайві (${unexpected.length})`}
          value="unexpected"
        />
        <Tab
          label={`Знайдено (${found.length})`}
          value="found"
        />
        {outOfScope.length > 0 && (
          <Tab
            label={`Поза фільтром (${outOfScope.length})`}
            value="outOfScope"
          />
        )}
      </Tabs>

      {rows.length === 0 ? (
        <Box sx={{ py: 3 }}>
          <Typography
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          >
            {EMPTY_MESSAGES[tab]}
          </Typography>
        </Box>
      ) : (
        <List>
          {rows.map((row) => (
            <ListItem
              key={row.key}
              divider
              secondaryAction={
                canEdit && row.scanId !== undefined ? (
                  <IconButton
                    edge="end"
                    color="error"
                    aria-label="Видалити скан"
                    onClick={() => onRemoveScan(row.scanId as number)}
                  >
                    <DeleteIcon />
                  </IconButton>
                ) : undefined
              }
            >
              <ListItemText
                primary={row.serialNumber}
                secondary={row.secondary || undefined}
                slotProps={{ primary: { sx: { wordBreak: 'break-all' } } }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
}
